/**
 * Dispute Workflow Hooks
 *
 * React Query hooks for dispute resolution lifecycle operations.
 * DevStory 4.5: Sistema de resolución de disputas con participación de la DAO.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectKeys } from '@hooks/useProjects';
import {
  openDispute,
  getDispute,
  getDisputesForMilestone,
  addDisputeArgument,
  getDisputeArguments,
  getDisputeHistory,
  getDisputeMessages,
  sendDisputeMessage,
  resolveDisputeDAO,
} from '@/services';
import type {
  Dispute,
  DisputeArgument,
  DisputeArgumentType,
  DisputeHistory,
  DisputeMessage,
} from '@/types';
import { useAuthStore } from '@/stores/authStore';

export const disputeKeys = {
  all: ['disputes'] as const,
  detail: (id: string) => [...disputeKeys.all, 'detail', id] as const,
  byMilestone: (projectId: string, milestoneId: string) =>
    [...disputeKeys.all, 'milestone', projectId, milestoneId] as const,
  arguments: (disputeId: string) =>
    [...disputeKeys.all, 'arguments', disputeId] as const,
  history: (disputeId: string) =>
    [...disputeKeys.all, 'history', disputeId] as const,
  messages: (disputeId: string) =>
    [...disputeKeys.all, 'messages', disputeId] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Query for a single dispute by ID.
 */
export function useDispute(disputeId?: string) {
  return useQuery<Dispute, Error>({
    queryKey: disputeKeys.detail(disputeId ?? ''),
    queryFn: async () => {
      if (!disputeId) throw new Error('Dispute ID is required');
      return getDispute(disputeId);
    },
    enabled: Boolean(disputeId),
  });
}

/**
 * Query for disputes associated with a specific milestone.
 */
export function useDisputesByMilestone(
  projectId?: string,
  milestoneId?: string
) {
  return useQuery<Dispute[], Error>({
    queryKey: disputeKeys.byMilestone(projectId ?? '', milestoneId ?? ''),
    queryFn: async () => {
      if (!projectId || !milestoneId) return [];
      return getDisputesForMilestone(projectId, milestoneId);
    },
    enabled: Boolean(projectId && milestoneId),
  });
}

/**
 * Query for all arguments in a dispute.
 */
export function useDisputeArguments(disputeId?: string) {
  return useQuery<DisputeArgument[], Error>({
    queryKey: disputeKeys.arguments(disputeId ?? ''),
    queryFn: async () => {
      if (!disputeId) return [];
      return getDisputeArguments(disputeId);
    },
    enabled: Boolean(disputeId),
    refetchInterval: 5000,
  });
}

/**
 * Query for full dispute history and evidences.
 */
export function useDisputeHistory(disputeId?: string) {
  return useQuery<DisputeHistory, Error>({
    queryKey: disputeKeys.history(disputeId ?? ''),
    queryFn: async () => {
      if (!disputeId)
        return { deliverables: [], reviews: [], rejections: [], events: [] };
      return getDisputeHistory(disputeId);
    },
    enabled: Boolean(disputeId),
  });
}

/**
 * Query for dispute communication messages.
 */
export function useDisputeMessages(disputeId?: string, enabled = true) {
  const principal = useAuthStore((s) => s.session?.principalId);
  return useQuery({
    queryKey: [...disputeKeys.messages(disputeId ?? ''), principal],
    queryFn: async () => {
      if (!disputeId) return { messages: [], canWrite: false };
      return getDisputeMessages(disputeId);
    },
    enabled: Boolean(disputeId) && enabled,
    refetchInterval: 3000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface CreateDisputeInput {
  projectId: string;
  milestoneId: string;
  reason: string;
}

/**
 * Mutation to open a new dispute on a rejected milestone.
 */
export function useCreateDispute() {
  const queryClient = useQueryClient();

  return useMutation<Dispute, Error, CreateDisputeInput>({
    mutationFn: async ({ projectId, milestoneId, reason }) => {
      return openDispute(projectId, milestoneId, reason);
    },
    onSuccess: (dispute, variables) => {
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.byMilestone(
          variables.projectId,
          variables.milestoneId
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.detail(dispute.id),
      });
      void queryClient.invalidateQueries({ queryKey: ['current-projects'] });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.detail(variables.projectId),
      });
    },
  });
}

export interface AddDisputeArgumentInput {
  disputeId: string;
  content: string;
  argumentType: DisputeArgumentType;
}

/**
 * Mutation to add an argument (counter-argument or additional evidence) to a dispute.
 */
export function useAddDisputeArgument() {
  const queryClient = useQueryClient();

  return useMutation<DisputeArgument, Error, AddDisputeArgumentInput>({
    mutationFn: async ({ disputeId, content, argumentType }) => {
      return addDisputeArgument(disputeId, content, argumentType);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.arguments(variables.disputeId),
      });
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.detail(variables.disputeId),
      });
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.history(variables.disputeId),
      });
    },
  });
}

export interface SendDisputeMessageInput {
  disputeId: string;
  content: string;
}

/**
 * Mutation to send a message in the dispute communication channel.
 */
export function useSendDisputeMessage() {
  const queryClient = useQueryClient();

  return useMutation<DisputeMessage, Error, SendDisputeMessageInput>({
    mutationFn: async ({ disputeId, content }) => {
      return sendDisputeMessage(disputeId, content);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.messages(variables.disputeId),
      });
    },
  });
}

export interface ResolveDisputeInput {
  disputeId: string;
  projectId?: string;
  outcome: 'RESOLVED' | 'REJECTED';
  winner?: 'client' | 'professional';
  winnerName?: string;
  reason: string;
  documentation?: string;
  otherLinks?: string;
  notes?: string;
}

/**
 * Mutation to resolve a dispute (DAO / Arbitrator).
 */
export function useResolveDispute() {
  const queryClient = useQueryClient();

  return useMutation<Dispute, Error, ResolveDisputeInput>({
    mutationFn: async ({
      disputeId,
      outcome,
      reason,
      notes,
      winner,
      winnerName,
      documentation,
      otherLinks,
    }) => {
      const token = useAuthStore.getState().token || '';
      return resolveDisputeDAO(
        disputeId,
        outcome,
        reason,
        notes,
        token,
        winner,
        winnerName,
        documentation,
        otherLinks
      );
    },
    onSuccess: (_dispute, variables) => {
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.detail(variables.disputeId),
      });
      void queryClient.invalidateQueries({
        queryKey: disputeKeys.history(variables.disputeId),
      });
      if (variables.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.detail(variables.projectId),
        });
      }
    },
  });
}
